import React from 'react';
import {useTranslation} from 'react-i18next';
import numeral from 'numeral';
import CustomTable from 'components/CustomTable';
import {Panel} from 'components/ui';
import {useMostActiveTraders} from 'hooks/datahook/useIbAgent';

/**
 * M2 (2026-10-20): "Most active traders" panel for the Clients & Sub IBs page.
 * Design-system Panel wrapping the system CustomTable (`noCard`, same shape as
 * RecentTrades / MonthlyCommissionPanel) — reskinned pages must not hand-roll
 * a <table>, so the rsuite-based table is the single table implementation.
 */
const MostActiveTraders = ({id}) => {
    const {t} = useTranslation('app');
    // `id` is optional: the IB whose sub-levels are ranked. Without it the
    // hook keeps its client-side behaviour (the session user's own tree).
    const swr = useMostActiveTraders({limit: 5, days: 30, userId: id});
    const rows = Array.isArray(swr?.data) ? swr.data : [];

    const column = [
        {
            label: t('client'),
            dataKey: 'name',
            columProps: {align: 'left', flexGrow: 1, minWidth: 110},
        },
        {
            label: t('account'),
            dataKey: 'login',
            columProps: {align: 'left', flexGrow: 1, minWidth: 90},
            renderCell: (row) => <span className="mono">{row.login}</span>,
        },
        {
            label: t('campaignColumn'),
            dataKey: 'campaignName',
            columProps: {align: 'left', flexGrow: 1, minWidth: 110},
            renderCell: (row) =>
                row.campaignName || t('defaultCampaignName'),
        },
        {
            label: t('volumeLots'),
            dataKey: 'volume',
            columProps: {align: 'right', flexGrow: 1, minWidth: 90},
            renderCell: (row) => (
                <span className="mono">
                    {numeral(row.volume).format('0.0')}
                </span>
            ),
        },
        {
            label: t('mostTradedInstruments'),
            dataKey: 'symbols',
            columProps: {align: 'left', flexGrow: 1, minWidth: 130},
            renderCell: (row) =>
                row.symbols?.length ? row.symbols.join(', ') : '—',
        },
        {
            label: t('rebatesGenerated'),
            dataKey: 'rebate',
            columProps: {align: 'right', flexGrow: 1, minWidth: 110},
            renderCell: (row) => (
                <span className="mono">
                    {row.rebateCurrency
                        ? `${row.rebateCurrency} ${numeral(row.rebate).format(
                              '0,0.00',
                          )}`
                        : '—'}
                </span>
            ),
        },
    ];

    return (
        <div
            className="nx"
            style={{
                minHeight: 'auto',
                background: 'transparent',
                marginBottom: 14,
            }}
        >
            <Panel
                title={t('mostActiveTraders')}
                sub={t('rankedByTradingVolume30d')}
            >
                <CustomTable
                    noCard
                    column={column}
                    data={rows}
                    loading={swr?.isLoading}
                    height={112}
                />
            </Panel>
        </div>
    );
};

export default MostActiveTraders;
